import Layout from "../Layout/Layout";
import ImageGallery from "../../components/ImageGallery/ImageGallery";
import { FaTrailer } from "react-icons/fa6";
import { PiToilet, PiOven } from "react-icons/pi";
import { TbRating21Plus } from "react-icons/tb";
import {
  MdOutlineUpcoming,
  MdPets,
  MdOutlineBed,
  MdBatteryChargingFull,
  MdSunnySnowing,
} from "react-icons/md";
import { JsonLDData } from "react-safe-json-ld";
import { FAQItem } from "../../components/FAQs/FAQItem";
import FAQs from "../../components/FAQs/FAQs";
import BookingCard from "../../components/Quote/BookingCard";

const SerenityDetail = () => {
  const FAQData: FAQItem[] = [
    {
      question: "What is Serenity best suited for?",
      answer:
        "Calm, zen-style getaways focused on hiking, fishing, exploring, remote work, and peaceful boondocking.",
    },
    {
      question: "How many people can Serenity sleep?",
      answer: "Comfortably sleeps 3 - 4 adults.",
    },
    {
      question: "How long can Serenity stay off-grid?",
      answer:
        "Over a week off-grid with its large solar panels, batteries, and water supplies.",
    },
    {
      question: "Is the interior really zen and calm?",
      answer:
        "Yes. Serenity is intentionally designed with a peaceful, minimalist interior for relaxation and focus.",
    },
    {
      question: "Can Serenity tow a boat or trailer?",
      answer:
        "Yes, up to 10,000 lbs with its frame-mounted receiver on the Ford E450 chassis.",
    },
    {
      question: "Where can I pick up Serenity?",
      answer: "Pickup is in Richardson, Texas.",
    },
    {
      question: "Can I book Serenity now?",
      answer:
        "Not yet — she arrives November 2026. You can pre-book dates as interest. That is not a paid reservation. We’ll email when she is ready to rent.",
    },
  ];

  const asBuilt: { src: string; alt: string }[] = [
    {
      src: "/images/fleet/serenity/dji_fly_20260726_110210_0006_1785084760932_photo.jpg",
      alt: "Serenity from the front three-quarter, coated gray with roof solar",
    },
    {
      src: "/images/fleet/serenity/dji_fly_20260726_110200_0005_1785084761183_photo.jpg",
      alt: "Serenity head-on, Ford E-450 grille and overcab",
    },
    {
      src: "/images/fleet/serenity/dji_fly_20260726_110136_0004_1785084759978_photo.jpg",
      alt: "Serenity passenger side in a park lot",
    },
    {
      src: "/images/fleet/serenity/20261020_151158.jpg",
      alt: "Serenity passenger side at the house, solar panels raised",
    },
    {
      src: "/images/fleet/serenity/20261020_151209.jpg",
      alt: "Driver's door and green captain's chair",
    },
    {
      src: "/images/fleet/serenity/dji_fly_20260726_110244_0007_1785084760730_photo.jpg",
      alt: "Serenity rear quarter with the condenser and hitch",
    },
    {
      src: "/images/fleet/serenity/dji_fly_20260726_110318_0008_1785084760450_photo.jpg",
      alt: "Overhead view of Serenity and the roof solar array",
    },
    {
      src: "/images/fleet/serenity/dji_fly_20260726_110352_0009_1785084760376_photo.jpg",
      alt: "Overhead rear view showing solar panels and the rear condenser",
    },
    {
      src: "/images/fleet/serenity/20261020_151229.jpg",
      alt: "Cabin aisle, bench platform, and galley",
    },
    {
      src: "/images/fleet/serenity/20261020_151231.jpg",
      alt: "Bench platform, overhead cabinets, and mini-split",
    },
    {
      src: "/images/fleet/serenity/20261020_151237.jpg",
      alt: "Dinette and propane range",
    },
    {
      src: "/images/fleet/serenity/20261020_151221.jpg",
      alt: "Wet bath with toilet and wave tile",
    },
  ];

  const started: { src: string; alt: string }[] = [
    {
      src: "/images/fleet/serenity/content.jpg",
      alt: "Serenity as a white shuttle bus, before the conversion",
    },
    {
      src: "/images/fleet/serenity/2024-11-12 13.31.55.jpg",
      alt: "Original shuttle interior with bus seats and a service counter",
    },
  ];

  const images: string[] = asBuilt.map(
    (photo) => `https://www.earthboundmartian.com${photo.src}`
  );

  const structuredData: JsonLDData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Product",
        name: "Serenity — Off-Grid Landship",
        description:
          "Serenity is a rugged off-grid Landship on a Ford E450 chassis. Calm and zen interior sleeps 3-4 comfortably, with over a week of boondocking capability. Perfect for peaceful hiking, fishing, and remote work getaways within 350 miles of DFW. Can tow a boat or trailer.",
        brand: { "@type": "Brand", name: "Earthbound Martian" },
        image: images,
        offers: { "@type": "Offer", priceCurrency: "USD", price: "220" },
      },
      {
        "@type": "FAQPage",
        mainEntity: FAQData.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <Layout structuredData={structuredData}>
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero Image + Title */}
        <div className="relative rounded-3xl overflow-hidden mb-8 lg:mb-12">
          <img
            src={asBuilt[0].src}
            alt={asBuilt[0].alt}
            className="w-full h-[280px] sm:h-[400px] lg:h-[500px] object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 sm:bottom-10 sm:left-10 sm:right-10 text-white">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold">Serenity</h1>
            <p className="text-lg sm:text-xl lg:text-2xl mt-2">
              Rugged • Powerful • Luxurious | Arriving November 2026
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 lg:gap-x-16 gap-8 lg:gap-y-12">
          <div className="lg:col-start-2 lg:row-start-2 lg:sticky lg:top-24 h-fit min-w-0">
            <BookingCard
              rig="serenity"
              heading="Arriving November 2026"
              prebook
              availableFrom="2026-11-01"
            />
          </div>

          {/* Quick Icons */}
          <div className="flex flex-wrap gap-6 lg:col-span-2 lg:row-start-1">
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <MdOutlineUpcoming size={24} />
            </span>
            <div>
              <div className="font-semibold">Coming Soon!</div>
              <div className="text-md text-zinc-500">Premiering November 2026</div>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <MdPets size={24} />
            </span>
            <div className="font-semibold">Pet Friendly</div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <TbRating21Plus size={24} />
            </span>
            <div className="font-semibold">21+</div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <MdOutlineBed size={24} />
            </span>
            <div>
              <div className="font-semibold">Queen + Twin</div>
              <div className="text-md text-zinc-500">Sleeps 3-4</div>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <FaTrailer size={24} />
            </span>
            <div>
              <div className="font-semibold">Towing</div>
            </div>
          </div>
        </div>

          <div className="lg:col-start-1 lg:row-start-2 min-w-0">
            <p className="text-xl text-zinc-600 leading-relaxed mb-10">
              A powerful 2011 Glaval E450 V10 bus conversion built to go
              anywhere in America.
              <br />
              <br />
              Rugged on the outside with serious towing power and presence.
              Elegant and luxuriously comfortable inside; captain’s chairs up
              front, a full wet bath with shower and toilet, a full kitchen with
              propane stove and oven, and on-demand hot water.
              <br />
              <br />
              The full-size sofa converts to a queen bed, the dinette becomes a
              twin, and large windows flood the space with light while thick
              spray foam insulation keeps it quiet and comfortable year-round.
              <br />
              <br />
              1500W solar + 550Wh battery keeps you powered for days off-grid.
              <br />
              <br />
              Serenity is your mobile home and office - rugged, powerful, and
              refined - ready to take you across the country in comfort.
            </p>

            <div className="text-xl mb-10 font-bold">
              ** All Features are aspirational at this time **
            </div>

            {/* Vehicle Info */}
            <div className="mb-12">
              <h3 className="font-semibold text-xl mb-6">Vehicle Info</h3>
              <div className="grid grid-cols-2 gap-x-12 gap-y-4 text-sm">
                <div>
                  <span className="text-lg text-zinc-500">Length:</span> 26 ft
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Transmission:</span>{" "}
                  Automatic
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Fuel Type:</span> Gas
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Range:</span> ~ 400
                  miles
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Weight:</span> 15,000
                  lbs
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Sleeps:</span> 3-4
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="mb-12">
              <h3 className="font-semibold text-xl mb-8">Amenities</h3>

              <div className="grid md:grid-cols-2 gap-x-16 gap-y-10">
                {/* Bathroom */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <PiToilet size={24} />
                    </span>{" "}
                    Bathroom
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• Fully Enclosed Wet Shower</li>
                    <li>• Cassette Toilet</li>
                    <li>• Venting</li>
                    <li>• Tankless Water Heater</li>
                  </ul>
                </div>

                {/* Kitchen */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <PiOven size={24} />
                    </span>{" "}
                    Kitchen
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• Propane oven and stove</li>
                    <li>• Sink with running water</li>
                    <li>• 10 cu ft Fridge</li>
                    <li>• 46 Gallon Fresh Water Tank</li>
                    <li>• Convertible Dinnette</li>
                  </ul>
                </div>

                {/* Power & Hookups */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <MdBatteryChargingFull />
                    </span>{" "}
                    Power & Hookups
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• 1500W Solar + 550Wh Battery</li>
                    <li>• Shore power hookup</li>
                    <li>• External Utility Plug</li>
                    <li>• Charge from Chassis</li>
                    <li>• Battery Boost</li>
                  </ul>
                </div>

                {/* Climate */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <MdSunnySnowing />{" "}
                    </span>{" "}
                    Climate & Comfort
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• 12k BTU Mini Split</li>
                    <li>• Propane Powered Heater</li>
                    <li>• 2" Insulation</li>
                    <li>• Thermal Curtains</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-24">
          <h3 className="text-2xl font-semibold mb-8">As built</h3>
          <ImageGallery
            images={asBuilt.map((photo) => photo.src)}
            alts={asBuilt.map((photo) => photo.alt)}
          />
        </div>

        <section className="mt-16 p-6 sm:p-8 bg-zinc-100 rounded-3xl">
          <h3 className="text-2xl font-semibold mb-3">Where she started</h3>
          <p className="text-lg text-zinc-600 mb-8 max-w-3xl">
            A white 2011 Glaval shuttle, before the conversion.
          </p>
          <ImageGallery
            images={started.map((photo) => photo.src)}
            alts={started.map((photo) => photo.alt)}
          />
        </section>

        <section className="flex mt-10 p-5 bg-zinc-100 rounded-xl">
          <FAQs faqs={FAQData} />
        </section>
      </div>
    </Layout>
  );
};

export default SerenityDetail;
